import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { suppliersApi, reviewsApi, type Supplier, ApiError } from "@/lib/api";

type ReviewRecommendation = "Auto-Renew" | "Conditional Renewal" | "Re-Advertise";

const SCORE_KEYS = [
  { key: "quality_of_service", label: "Quality of Service" },
  { key: "timeliness_delivery", label: "Timeliness & Delivery" },
  { key: "communication", label: "Communication" },
  { key: "value_for_money", label: "Value for Money" },
  { key: "compliance", label: "Compliance" },
];

const SCORE_LABELS: Record<number, string> = { 1: "Poor", 2: "Below Avg", 3: "Adequate", 4: "Good", 5: "Excellent" };

const RECOMMENDATIONS: { value: ReviewRecommendation; label: string; desc: string }[] = [
  { value: "Auto-Renew", label: "Auto-Renew", desc: "Supplier meets or exceeds all expectations — approve renewal" },
  { value: "Conditional Renewal", label: "Conditional Renewal", desc: "Renew with specific improvement conditions attached" },
  { value: "Re-Advertise", label: "Re-Advertise", desc: "Performance unacceptable — initiate new procurement process" },
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];

export default function NewReview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get("supplier_id") ? Number(searchParams.get("supplier_id")) : null;

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [supplierId, setSupplierId] = useState<number | "">(preselectedId ?? "");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [quarter, setQuarter] = useState(1);
  const [year, setYear] = useState(currentYear);
  const [scores, setScores] = useState<Record<string, number>>(
    Object.fromEntries(SCORE_KEYS.map((k) => [k.key, 3]))
  );
  const [comments, setComments] = useState("");
  const [recommendation, setRecommendation] = useState<ReviewRecommendation>("Conditional Renewal");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    suppliersApi.list(1, { search: undefined }).then((res) => {
      setSuppliers(res.data.data);
      if (preselectedId) {
        const found = res.data.data.find((s) => s.id === preselectedId);
        if (found) setSupplierSearch(found.name);
      }
    }).catch(() => {}).finally(() => setLoadingSuppliers(false));
  }, []);

  const filteredSuppliers = useMemo(
    () => supplierSearch ? suppliers.filter((s) => s.name.toLowerCase().includes(supplierSearch.toLowerCase())) : suppliers,
    [suppliers, supplierSearch]
  );

  const selectedSupplier = suppliers.find((s) => s.id === supplierId);

  const overallScore = useMemo(() => {
    const vals = Object.values(scores);
    return vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : 0;
  }, [scores]);

  const isValid = supplierId && comments.trim();

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const reviewDate = `${year}-${String(quarter * 3).padStart(2, "0")}-01`;
      await reviewsApi.create({
        supplier_id: supplierId as number,
        rating: String(overallScore),
        review_text: comments,
        review_date: reviewDate,
        scores,
        recommendation,
        quarter,
        year,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 rounded-full bg-secondary-50 flex items-center justify-center mb-4">
          <i className="ri-check-line text-secondary-500 text-2xl"></i>
        </div>
        <h2 className="text-primary-600 text-lg font-bold mb-1">Review Submitted</h2>
        <p className="text-foreground-500 text-sm mb-6">
          Quarterly review for {selectedSupplier?.name ?? "supplier"} has been recorded.
        </p>
        <div className="flex gap-2">
          <button onClick={() => navigate("/supplier-reviews")}
            className="h-9 px-4 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors cursor-pointer">
            Back to Supplier Reviews
          </button>
          {selectedSupplier && (
            <button onClick={() => navigate(`/supplier-reviews/${selectedSupplier.id}`)}
              className="h-9 px-4 border border-background-300 text-foreground-600 text-sm font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer">
              View Performance
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/supplier-reviews")}
            className="w-8 h-8 rounded-md flex items-center justify-center border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-sm"></i>
          </button>
          <div>
            <h1 className="text-primary-600 text-xl md:text-2xl font-bold">New Quarterly Review</h1>
            <p className="text-foreground-500 text-sm mt-1">Rate supplier performance across all categories</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 bg-accent-50 border border-accent-200 rounded-lg px-4 py-3">
          <i className="ri-error-warning-line text-accent-600 text-sm"></i>
          <p className="text-accent-700 text-sm">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Supplier & Period */}
          <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Supplier */}
              <div className="sm:col-span-2 relative">
                <label className="block text-foreground-500 text-xs font-medium uppercase tracking-wider mb-1.5">
                  Supplier <span className="text-accent-500">*</span>
                </label>
                <input type="text" placeholder={loadingSuppliers ? "Loading suppliers..." : "Search supplier..."}
                  value={selectedSupplier ? selectedSupplier.name : supplierSearch}
                  onChange={(e) => { setSupplierId(""); setSupplierSearch(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  disabled={loadingSuppliers}
                  className="w-full h-9 px-3 text-sm border border-background-300 rounded-md bg-white text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors disabled:opacity-60" />
                {showDropdown && !loadingSuppliers && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-md border border-background-200 shadow-lg z-20 max-h-48 overflow-y-auto">
                    {filteredSuppliers.length > 0 ? filteredSuppliers.map((s) => (
                      <button key={s.id} onMouseDown={() => { setSupplierId(s.id); setSupplierSearch(s.name); setShowDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-background-100 transition-colors cursor-pointer ${s.id === supplierId ? "bg-primary-50 text-primary-600 font-medium" : "text-foreground-700"}`}>
                        {s.name}
                      </button>
                    )) : (
                      <div className="px-3 py-2 text-sm text-foreground-400">No suppliers found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Year */}
              <div>
                <label className="block text-foreground-500 text-xs font-medium uppercase tracking-wider mb-1.5">Year</label>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                  className="w-full h-9 px-3 text-sm border border-background-300 rounded-md bg-white text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer">
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              {/* Quarter */}
              <div>
                <label className="block text-foreground-500 text-xs font-medium uppercase tracking-wider mb-1.5">Quarter</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((q) => (
                    <button key={q} onClick={() => setQuarter(q)}
                      className={`flex-1 h-9 text-sm font-medium rounded-md transition-colors cursor-pointer ${quarter === q ? "bg-primary-600 text-white" : "bg-background-100 text-foreground-600 hover:bg-background-200"}`}>
                      Q{q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Rating Categories */}
          <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
            <h2 className="text-primary-600 text-base font-semibold mb-4">Performance Ratings</h2>
            <div className="space-y-4">
              {SCORE_KEYS.map(({ key, label }) => (
                <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <div className="sm:w-48 shrink-0">
                    <p className="text-foreground-700 text-sm font-medium">{label}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button key={s} onClick={() => setScores((prev) => ({ ...prev, [key]: s }))}
                        className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors cursor-pointer ${scores[key] === s ? "bg-primary-600 text-white" : "bg-background-100 text-foreground-500 hover:bg-background-200"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                  <div className="sm:w-20 text-right shrink-0">
                    <span className={`text-sm font-semibold ${scores[key] >= 4 ? "text-secondary-600" : scores[key] >= 2.5 ? "text-accent-600" : "text-foreground-500"}`}>
                      {SCORE_LABELS[scores[key]]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
            <label className="block text-foreground-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Reviewer Comments <span className="text-accent-500">*</span>
            </label>
            <textarea value={comments} onChange={(e) => setComments(e.target.value)}
              placeholder="Provide detailed feedback on the supplier's performance this quarter. Note any specific incidents, achievements, or concerns..."
              maxLength={1000} rows={4}
              className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-white text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none" />
            <p className="text-foreground-400 text-xs mt-1 text-right">{comments.length}/1000</p>
          </div>
        </div>

        {/* Sidebar — Summary & Recommendation */}
        <div className="space-y-5">
          <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 sticky top-20">
            <h2 className="text-primary-600 text-base font-semibold mb-4">Review Summary</h2>

            {/* Overall Score */}
            <div className="flex flex-col items-center mb-5 pb-5 border-b border-background-200">
              <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-2">Overall Score</p>
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-2 ${overallScore >= 4 ? "bg-secondary-500" : overallScore >= 2.5 ? "bg-accent-500" : "bg-foreground-400"}`}>
                <span className="text-white text-2xl font-bold">{overallScore}</span>
              </div>
              <p className="text-foreground-500 text-xs">out of 5.0</p>
            </div>

            {/* Recommendation */}
            <div className="mb-5">
              <p className="text-foreground-500 text-xs font-medium uppercase tracking-wider mb-2">Renewal Recommendation</p>
              <div className="space-y-2">
                {RECOMMENDATIONS.map((rec) => (
                  <button key={rec.value} onClick={() => setRecommendation(rec.value)}
                    className={`w-full text-left p-3 rounded-md border transition-colors cursor-pointer ${recommendation === rec.value ? "border-primary-400 bg-primary-50" : "border-background-200 hover:border-background-300"}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${recommendation === rec.value ? "border-primary-500" : "border-background-300"}`}>
                        {recommendation === rec.value && <div className="w-2 h-2 rounded-full bg-primary-500"></div>}
                      </div>
                      <span className={`text-sm font-semibold ${recommendation === rec.value ? "text-primary-600" : "text-foreground-700"}`}>{rec.label}</span>
                    </div>
                    <p className="text-foreground-400 text-xs ml-5">{rec.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handleSubmit} disabled={!isValid || submitting}
              className={`w-full h-10 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 cursor-pointer ${isValid ? "bg-primary-600 text-white hover:bg-primary-700" : "bg-background-200 text-foreground-400 cursor-not-allowed"}`}>
              {submitting ? <i className="ri-loader-4-line animate-spin text-sm"></i> : <i className="ri-check-line text-sm"></i>}
              <span>{submitting ? "Submitting..." : "Submit Review"}</span>
            </button>
            {!isValid && (
              <p className="text-foreground-400 text-xs text-center mt-2">Select a supplier and add comments to submit</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
